import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Flex, Text, Badge, SimpleGrid, Input, InputGroup, InputLeftElement, Select, Switch,
    FormControl, FormLabel, FormErrorMessage, Button, IconButton, Tooltip, Table, Thead, Tbody, Tr, Th, Td,
    Skeleton, VStack, HStack, Stack, Icon, Alert, AlertIcon, useToast,
} from '@chakra-ui/react';
import { Search, FileCode, FileDown, FileX, ShieldCheck, FileText, RefreshCw } from 'lucide-react';
import {
    getNfeDocumentos, getNfeDocumentosResumo, getEmitentesXmlNfe, baixarArquivoNfeDocumento, consultarNfeDocumento,
} from '../services/api';
import { formatBRL, formatDateTime, formatDate } from '../utils/formatters';
import { textoDeErro } from '../utils/erroApi';
import {
    TIPOS_NOTA, TIPO_NOTA, ORIGENS_NOTA, formatarCnpj, formatarDocumento, dataLocal, validarPeriodo, nomeArquivoNota,
} from '../utils/notasFiscais';

const ACCENT = '#F68633';
const FOCUS_RING = '0 0 0 3px rgba(246,134,51,0.18)';

const periodoPadrao = () => {
    const hoje = new Date();
    return { data_inicio: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), data_fim: dataLocal(hoje) };
};

const ehDaTaga = (doc) => ['48921058', '56113692'].includes(String(doc.chave).slice(6, 14)) && doc.origem !== 'terceiros';

const TipoBadge = ({ tipo }) => {
    const t = TIPO_NOTA[tipo];
    return (
        <Badge colorScheme={t?.cor || 'gray'} fontSize="10px" px={2} py={0.5} borderRadius="md" whiteSpace="normal" textAlign="left">
            {t?.curto || tipo}
        </Badge>
    );
};

const Situacao = ({ doc }) => (
    <Stack spacing={1} align="flex-start">
        <Badge colorScheme={doc.status === 'cancelada' ? 'red' : 'green'} fontSize="10px" px={2} py={0.5} borderRadius="md">
            {doc.status === 'cancelada' ? 'Cancelada' : 'Autorizada'}
        </Badge>
        {doc.status === 'cancelada' && doc.cancelada_em && (
            <Text fontSize="10px" color="gray.500">em {formatDate(doc.cancelada_em, '')}</Text>
        )}
        {!doc.tem_xml && (
            <Badge colorScheme="orange" variant="outline" fontSize="10px" px={2} py={0.5} borderRadius="md">Sem XML</Badge>
        )}
    </Stack>
);

const Contraparte = ({ doc }) => {
    if (doc.tipo === 'carta_correcao') {
        return (
            <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap" wordBreak="break-word">
                {doc.observacao || 'Texto da correção não informado'}
            </Text>
        );
    }

    const terceiro = doc.origem === 'terceiros';
    const nome = terceiro ? doc.emitente_nome : doc.destinatario_nome;
    const documento = terceiro ? doc.emitente_documento : doc.destinatario_documento;

    return (
        <Box>
            <Text fontSize="xs" fontWeight="semibold" color="gray.800" wordBreak="break-word">{nome || 'Sem nome na nota'}</Text>
            {documento && <Text fontSize="11px" color="gray.500">{formatarDocumento(documento)}</Text>}
            {doc.pedido && (
                <Text fontSize="11px" color="gray.600">
                    Pedido {doc.pedido}{doc.venda_origem ? ` (${doc.venda_origem})` : ''}
                </Text>
            )}
        </Box>
    );
};

const Identificacao = ({ doc }) => (
    <Box>
        <HStack spacing={2} wrap="wrap" mb={1}>
            <TipoBadge tipo={doc.tipo} />
            <Text fontSize="xs" fontWeight="bold" color="gray.800">
                Nº {doc.numero || '?'} · Série {doc.serie || '?'}
                {doc.tipo === 'carta_correcao' ? ` · CC-e ${doc.sequencia}` : ''}
            </Text>
        </HStack>
        <Text fontSize="10px" fontFamily="mono" color="gray.600" wordBreak="break-all">{doc.chave}</Text>
        <Text fontSize="10px" color="gray.500">
            {ORIGENS_NOTA[doc.origem] || doc.origem} · {formatarCnpj(doc.cnpj_estabelecimento)}
        </Text>
    </Box>
);

const Acoes = ({ doc, ocupado, onBaixar, onConsultar, rotulos = false }) => {
    const botoes = [
        doc.tem_xml && { chave: 'xml', label: 'XML', icone: FileCode, acao: () => onBaixar(doc, 'xml') },
        doc.tem_xml && doc.tipo !== 'carta_correcao' && { chave: 'danfe', label: 'DANFE', icone: FileDown, acao: () => onBaixar(doc, 'danfe') },
        doc.tem_cancelamento && { chave: 'cancelamento', label: 'XML do cancelamento', icone: FileX, acao: () => onBaixar(doc, 'cancelamento') },
        doc.tipo !== 'carta_correcao' && ehDaTaga(doc) && { chave: 'consultar', label: 'Consultar na SEFAZ', icone: ShieldCheck, acao: () => onConsultar(doc) },
    ].filter(Boolean);

    if (botoes.length === 0) return null;

    if (rotulos) {
        return (
            <Flex gap={2} wrap="wrap">
                {botoes.map((b) => (
                    <Button key={b.chave} size="xs" variant="outline" leftIcon={<Icon as={b.icone} boxSize={3.5} />}
                        isLoading={ocupado === `${doc.id}-${b.chave}`} onClick={b.acao}>
                        {b.label}
                    </Button>
                ))}
            </Flex>
        );
    }

    return (
        <HStack spacing={1}>
            {botoes.map((b) => (
                <Tooltip key={b.chave} label={b.label} hasArrow>
                    <IconButton size="sm" variant="ghost" aria-label={b.label} icon={<Icon as={b.icone} boxSize={4} />}
                        isLoading={ocupado === `${doc.id}-${b.chave}`} onClick={b.acao} />
                </Tooltip>
            ))}
        </HStack>
    );
};

export default function TodasNotasFiscais({ filtrosIniciais }) {
    const toast = useToast();

    const [filtros, setFiltros] = useState(() => ({
        ...periodoPadrao(), tipos: [], status: '', origem: '', cnpj: '', pendentes: false, ...(filtrosIniciais || {}),
    }));
    const [busca, setBusca] = useState('');
    const [buscaDeb, setBuscaDeb] = useState('');
    const [pagina, setPagina] = useState(1);
    const [dados, setDados] = useState({ data: [], total: 0, last_page: 1 });
    const [resumo, setResumo] = useState([]);
    const [emitentes, setEmitentes] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [ocupado, setOcupado] = useState(null);

    const erroPeriodo = validarPeriodo(filtros);

    useEffect(() => {
        const t = setTimeout(() => {
            const valor = busca.trim();
            if (valor !== buscaDeb) {
                setPagina(1);
                setBuscaDeb(valor);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [busca, buscaDeb]);

    useEffect(() => {
        let ativo = true;
        getEmitentesXmlNfe()
            .then((lista) => { if (ativo) setEmitentes(Array.isArray(lista) ? lista : []); })
            .catch(() => { if (ativo) setEmitentes([]); });
        return () => { ativo = false; };
    }, []);

    const carregar = useCallback(async () => {
        if (erroPeriodo) return;
        setCarregando(true);
        setErro(null);

        const params = {
            data_inicio: filtros.data_inicio,
            data_fim: filtros.data_fim,
            tipos: filtros.tipos.length ? filtros.tipos : undefined,
            status: filtros.status || undefined,
            origem: filtros.origem || undefined,
            cnpj: filtros.cnpj || undefined,
            busca: buscaDeb || undefined,
            pendentes: filtros.pendentes ? 1 : undefined,
        };

        try {
            const [lista, res] = await Promise.all([
                getNfeDocumentos({ ...params, page: pagina, por_pagina: 30 }),
                getNfeDocumentosResumo({ ...params, tipos: undefined }),
            ]);
            setDados(lista || { data: [], total: 0, last_page: 1 });
            setResumo(res?.tipos || []);
        } catch (e) {
            setErro(textoDeErro(e?.response?.data) || e.message);
            setDados({ data: [], total: 0, last_page: 1 });
        } finally {
            setCarregando(false);
        }
    }, [filtros, buscaDeb, pagina, erroPeriodo]);

    useEffect(() => { carregar(); }, [carregar]);

    const alterar = (campo, valor) => {
        setPagina(1);
        setFiltros((f) => ({ ...f, [campo]: valor }));
    };

    const alternarTipo = (tipo) => {
        setPagina(1);
        setFiltros((f) => ({ ...f, tipos: f.tipos.length === 1 && f.tipos[0] === tipo ? [] : [tipo] }));
    };

    const baixar = async (doc, formato) => {
        setOcupado(`${doc.id}-${formato}`);
        const r = await baixarArquivoNfeDocumento(doc.id, formato, nomeArquivoNota(doc, formato));
        setOcupado(null);
        if (r?.error) {
            toast({ title: 'Não foi possível baixar', description: r.error, status: 'error', duration: 7000, isClosable: true });
        }
    };

    const consultar = async (doc) => {
        setOcupado(`${doc.id}-consultar`);
        const r = await consultarNfeDocumento(doc.id);
        setOcupado(null);
        if (r?.error) {
            toast({ title: 'Consulta na SEFAZ falhou', description: r.error, status: 'error', duration: 7000, isClosable: true });
            return;
        }
        const situacao = { autorizada: 'Autorizada', cancelada: 'Cancelada', denegada: 'Denegada', nao_encontrada: 'Não encontrada' }[r.situacao] || r.situacao;
        toast({
            title: `SEFAZ: ${situacao}`,
            description: `${r.cstat} ${r.motivo}${r.alterado ? '. Situação atualizada aqui.' : ''}`,
            status: r.situacao === 'autorizada' || r.situacao === 'cancelada' ? 'success' : 'warning',
            duration: 7000,
            isClosable: true,
        });
        if (r.alterado) carregar();
    };

    const lista = dados.data || [];

    return (
        <Box>
            <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} gap={3} mb={4}>
                {TIPOS_NOTA.map((t) => {
                    const r = resumo.find((x) => x.tipo === t.value) || { total: 0, valor: '0', canceladas: 0, sem_xml: 0 };
                    const ativo = filtros.tipos.length === 1 && filtros.tipos[0] === t.value;
                    return (
                        <Box key={t.value} bg="white" borderRadius="lg" p={3} border="1px solid"
                            borderColor={ativo ? `${t.cor}.400` : 'gray.200'} boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                            cursor="pointer" _hover={{ borderColor: `${t.cor}.300` }} transition="all 0.15s"
                            onClick={() => alternarTipo(t.value)}>
                            <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="semibold" letterSpacing="wide">
                                {t.label}
                            </Text>
                            <Text fontSize="xl" fontWeight="bold" color={`${t.cor}.600`}>{r.total}</Text>
                            {t.value !== 'carta_correcao' && <Text fontSize="xs" color="gray.700">{formatBRL(r.valor)}</Text>}
                            <Text fontSize="10px" color={r.canceladas ? 'red.500' : 'gray.400'}>{r.canceladas} cancelada(s)</Text>
                            <Text fontSize="10px" color={r.sem_xml ? 'orange.500' : 'gray.400'}>{r.sem_xml} sem XML</Text>
                        </Box>
                    );
                })}
            </SimpleGrid>

            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={{ base: 3, md: 4 }} mb={4}>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={3}>
                    <FormControl isInvalid={!!erroPeriodo}>
                        <FormLabel fontSize="xs" mb={1}>Emissão de</FormLabel>
                        <Input size="sm" type="date" value={filtros.data_inicio} max={filtros.data_fim || undefined}
                            onChange={(e) => alterar('data_inicio', e.target.value)} isDisabled={!!buscaDeb} />
                    </FormControl>
                    <FormControl isInvalid={!!erroPeriodo}>
                        <FormLabel fontSize="xs" mb={1}>Emissão até</FormLabel>
                        <Input size="sm" type="date" value={filtros.data_fim} min={filtros.data_inicio || undefined}
                            onChange={(e) => alterar('data_fim', e.target.value)} isDisabled={!!buscaDeb} />
                        {erroPeriodo && <FormErrorMessage>{erroPeriodo}</FormErrorMessage>}
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" mb={1}>Tipo de nota</FormLabel>
                        <Select size="sm" value={filtros.tipos.length === 1 ? filtros.tipos[0] : ''}
                            onChange={(e) => alterar('tipos', e.target.value ? [e.target.value] : [])}>
                            <option value="">Todos os tipos</option>
                            {TIPOS_NOTA.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" mb={1}>Situação</FormLabel>
                        <Select size="sm" value={filtros.status} onChange={(e) => alterar('status', e.target.value)}>
                            <option value="">Autorizadas e canceladas</option>
                            <option value="autorizada">Só autorizadas</option>
                            <option value="cancelada">Só canceladas</option>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" mb={1}>Origem</FormLabel>
                        <Select size="sm" value={filtros.origem} onChange={(e) => alterar('origem', e.target.value)}>
                            <option value="">Todas as origens</option>
                            {Object.entries(ORIGENS_NOTA).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs" mb={1}>Emitente</FormLabel>
                        <Select size="sm" value={filtros.cnpj} onChange={(e) => alterar('cnpj', e.target.value)}>
                            <option value="">Todos os emitentes</option>
                            {emitentes.map((e) => (
                                <option key={e.cnpj} value={e.cnpj}>{formatarCnpj(e.cnpj)}{e.nome ? ` · ${e.nome}` : ''}</option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl gridColumn={{ base: 'auto', lg: 'span 2' }}>
                        <FormLabel fontSize="xs" mb={1}>Buscar em todos os períodos</FormLabel>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none"><Search size={14} color="#9CA3AF" /></InputLeftElement>
                            <Input placeholder="Chave, número, pedido, CPF/CNPJ ou nome" value={busca}
                                onChange={(e) => setBusca(e.target.value)} _focus={{ borderColor: ACCENT, boxShadow: FOCUS_RING }} />
                        </InputGroup>
                    </FormControl>
                </SimpleGrid>
                <Flex mt={3} justify="space-between" align="center" gap={3} wrap="wrap">
                    <FormControl display="flex" alignItems="center" w="auto">
                        <Switch id="so-sem-xml" colorScheme="orange" size="sm" isChecked={filtros.pendentes}
                            onChange={(e) => alterar('pendentes', e.target.checked)} />
                        <FormLabel htmlFor="so-sem-xml" mb={0} ml={2} fontSize="sm">Só notas sem XML</FormLabel>
                    </FormControl>
                    <Button size="sm" variant="outline" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando}>
                        Atualizar
                    </Button>
                </Flex>
            </Box>

            {erro && (
                <Alert status="error" borderRadius="md" mb={4} fontSize="sm"><AlertIcon />{erro}</Alert>
            )}

            {carregando ? (
                <VStack spacing={2} align="stretch">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} h="72px" borderRadius="lg" startColor="gray.100" endColor="gray.200" />)}
                </VStack>
            ) : lista.length === 0 ? (
                <Flex direction="column" align="center" py={12} gap={3}>
                    <Flex w="60px" h="60px" borderRadius="full" bg="gray.100" align="center" justify="center">
                        <Icon as={FileText} boxSize={7} color="gray.400" />
                    </Flex>
                    <Text color="gray.500" fontSize="sm">Nenhuma nota encontrada</Text>
                </Flex>
            ) : (
                <>
                    <Stack display={{ base: 'flex', lg: 'none' }} spacing={2}>
                        {lista.map((doc) => (
                            <Box key={doc.id} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={3}>
                                <Flex justify="space-between" align="flex-start" gap={3}>
                                    <Box minW={0} flex="1"><Identificacao doc={doc} /></Box>
                                    <Box flexShrink={0}><Situacao doc={doc} /></Box>
                                </Flex>
                                <Box mt={2}><Contraparte doc={doc} /></Box>
                                <Flex justify="space-between" align="center" mt={2} gap={2}>
                                    <Text fontSize="11px" color="gray.500">{formatDateTime(doc.data_emissao, 'Sem data de emissão')}</Text>
                                    {doc.tipo !== 'carta_correcao' && <Text fontSize="sm" fontWeight="semibold">{formatBRL(doc.valor)}</Text>}
                                </Flex>
                                <Box mt={2}>
                                    <Acoes doc={doc} ocupado={ocupado} onBaixar={baixar} onConsultar={consultar} rotulos />
                                </Box>
                            </Box>
                        ))}
                    </Stack>

                    <Box display={{ base: 'none', lg: 'block' }} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflowX="auto">
                        <Table size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Emissão</Th>
                                    <Th>Nota</Th>
                                    <Th>Cliente, fornecedor ou correção</Th>
                                    <Th isNumeric>Valor</Th>
                                    <Th>Situação</Th>
                                    <Th>Arquivos</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {lista.map((doc) => (
                                    <Tr key={doc.id} verticalAlign="top">
                                        <Td fontSize="xs" whiteSpace="nowrap">{formatDateTime(doc.data_emissao, 'Sem data')}</Td>
                                        <Td minW="300px" maxW="340px"><Identificacao doc={doc} /></Td>
                                        <Td minW="220px"><Contraparte doc={doc} /></Td>
                                        <Td isNumeric fontSize="xs" whiteSpace="nowrap">{doc.tipo === 'carta_correcao' ? '' : formatBRL(doc.valor)}</Td>
                                        <Td><Situacao doc={doc} /></Td>
                                        <Td><Acoes doc={doc} ocupado={ocupado} onBaixar={baixar} onConsultar={consultar} /></Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>

                    <Flex justify="space-between" align="center" mt={4} gap={3} wrap="wrap">
                        <Text fontSize="xs" color="gray.600">
                            {dados.total} nota(s) · página {dados.current_page || pagina} de {dados.last_page || 1}
                        </Text>
                        <HStack spacing={2}>
                            <Button size="sm" variant="outline" isDisabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
                            <Button size="sm" variant="outline" isDisabled={pagina >= (dados.last_page || 1)} onClick={() => setPagina((p) => p + 1)}>Próxima</Button>
                        </HStack>
                    </Flex>
                </>
            )}
        </Box>
    );
}
